import { useEffect, useRef, useState } from "react";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { useLocation } from "wouter";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { apiUrl, nativeAuthHeaders } from "@/lib/apiBase";
import { applyAppUpdate } from "@/lib/swUpdate";

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// Routes where we should NOT interrupt the user (active training session or multi-step flows)
const SENSITIVE_ROUTES = [
  "/client/train",
  "/client/onboarding",
  "/coach/forge",
];

function isSensitiveRoute(path: string): boolean {
  return SENSITIVE_ROUTES.some((r) => path.startsWith(r));
}

function hasUnsavedFormData(): boolean {
  // Check if any input/textarea has content that might be unsaved
  const inputs = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    "input:not([type=hidden]):not([type=checkbox]):not([type=radio]), textarea"
  );
  for (const el of inputs) {
    if (el.value && el.value.trim().length > 0) return true;
  }
  return false;
}

export function UpdateBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [forceReload, setForceReload] = useState(false);
  const [nativeUpdateRequired, setNativeUpdateRequired] = useState(false);
  const [suppressed, setSuppressed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const initialVersion = useRef<string | null>(null);
  const storeUrl = useRef<string | null>(null);
  const [location] = useLocation();

  const handleRefresh = () => {
    if (nativeUpdateRequired) {
      // A native (App Store / Play Store) build can't hot-reload its bundled
      // web assets — the user has to install the new build from the store.
      // Send them straight there; `_blank` routes to the system browser/store
      // app in Capacitor. Fall back to dismissing if no store URL is known.
      const url = storeUrl.current;
      if (url) {
        try { window.open(url, "_blank"); } catch { /* fall through to dismiss */ }
      }
      setSuppressed(true);
      return;
    }
    setRefreshing(true);
    void applyAppUpdate();
  };

  useEffect(() => {
    const fetchVersion = async (): Promise<{
      version: string;
      forceReload: boolean;
      nativeMinimumBuilds?: { android?: number; ios?: number };
      updateUrls?: { android?: string | null; ios?: string | null };
    } | null> => {
      try {
        const res = await fetch(apiUrl("/api/version"), {
          cache: "no-store",
          headers: nativeAuthHeaders(),
          credentials: "include",
        });
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    };

    const checkNativeBuild = async (data: Awaited<ReturnType<typeof fetchVersion>>) => {
      if (!data || !Capacitor.isNativePlatform()) return false;
      const platform = Capacitor.getPlatform();
      const minimum =
        platform === "android"
          ? data.nativeMinimumBuilds?.android
          : platform === "ios"
          ? data.nativeMinimumBuilds?.ios
          : undefined;
      storeUrl.current =
        (platform === "android" ? data.updateUrls?.android : data.updateUrls?.ios) ?? storeUrl.current;
      if (!minimum || minimum <= 0) return false;

      try {
        const info = await CapacitorApp.getInfo();
        const currentBuild = Number.parseInt(String(info.build ?? "0"), 10);
        if (Number.isFinite(currentBuild) && currentBuild > 0 && currentBuild < minimum) {
          setNativeUpdateRequired(true);
          setForceReload(true);
          setUpdateAvailable(true);
          return true;
        }
      } catch {
        return false;
      }
      return false;
    };

    // Capture initial version at page load
    fetchVersion().then((data) => {
      if (data?.version) initialVersion.current = data.version;
      void checkNativeBuild(data);
    });

    const interval = setInterval(async () => {
      const data = await fetchVersion();
      if (!data) return;
      if (await checkNativeBuild(data)) return;

      // Force reload: auto-reload immediately (skip banner) unless on sensitive route
      if (data.forceReload) {
        if (!isSensitiveRoute(location) && !hasUnsavedFormData()) {
          setRefreshing(true);
          void applyAppUpdate();
        } else {
          // On sensitive route: show urgent banner instead of auto-reloading
          setForceReload(true);
          setUpdateAvailable(true);
        }
        return;
      }

      // Normal update: show banner if version changed
      if (
        data.version &&
        initialVersion.current &&
        data.version !== initialVersion.current
      ) {
        setUpdateAvailable(true);
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [location]);

  // Suppress banner on sensitive routes unless it's a force reload
  const isOnSensitiveRoute = isSensitiveRoute(location);
  const shouldShow = updateAvailable && (!isOnSensitiveRoute || forceReload || nativeUpdateRequired) && !suppressed;

  if (!shouldShow) return null;

  return (
    <div
      role="status"
      aria-live={forceReload || nativeUpdateRequired ? "assertive" : "polite"}
      data-standard-update-banner
      className="fixed inset-x-0 top-0 z-[9999] flex items-center gap-2 border-b border-white/15"
      style={{
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 8px)",
        paddingRight: "calc(env(safe-area-inset-right, 0px) + 12px)",
        paddingBottom: "8px",
        paddingLeft: "calc(env(safe-area-inset-left, 0px) + 12px)",
        background: forceReload
          ? "linear-gradient(90deg, #991b1b 0%, #dc2626 50%, #991b1b 100%)"
          : "linear-gradient(90deg, #B8860B 0%, #D4A017 50%, #B8860B 100%)",
        boxShadow: forceReload
          ? "0 2px 12px rgba(220,38,38,0.5)"
          : "0 2px 12px rgba(184,134,11,0.4)",
      }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2 text-white">
        {forceReload ? (
          <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
        ) : (
          <RefreshCw className={`size-4 shrink-0 ${refreshing ? "animate-spin" : ""}`} aria-hidden="true" />
        )}
        <span className="min-w-0 text-xs font-semibold leading-tight sm:text-sm">
          {refreshing
            ? "Refreshing app..."
            : nativeUpdateRequired
            ? "Update required. Install the latest app build."
            : forceReload
            ? "Critical update. Please refresh when ready."
            : "New update available"}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {!forceReload && !refreshing && (
          <button
            type="button"
            onClick={() => setSuppressed(true)}
            className="min-h-9 whitespace-nowrap rounded-md px-2 text-xs font-semibold text-white/80 transition-colors hover:bg-black/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
          >
            Later
          </button>
        )}
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className="min-h-9 whitespace-nowrap rounded-md border border-white/20 bg-black/30 px-3 text-xs font-bold text-white transition-all hover:bg-black/50 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:opacity-70 disabled:active:scale-100"
        >
          {refreshing ? "Refreshing" : nativeUpdateRequired ? "Update" : "Refresh Now"}
        </button>
      </div>
    </div>
  );
}
